import { useMemo, useState } from "react";
import {
  TutorialItem,
  tutorialCatalog,
} from "./cards-tutorial";
import { TutorialSidebar } from "./TutorialSidebar";

function extractDriveFolderId(driveLink?: string) {
  if (!driveLink) return "";

  const match = driveLink.match(/folders\/([^?/]+)/i);
  return match?.[1] || "";
}

function buildEmbeddedFolderUrl(
  driveLink?: string,
  mode: "grid" | "list" = "list"
) {
  const folderId = extractDriveFolderId(driveLink);

  if (!folderId) return "";

  return `https://drive.google.com/embeddedfolderview?id=${folderId}#${mode}`;
}

function buildDrivePreviewUrl(fileId?: string) {
  if (!fileId) return "";

  return `https://drive.google.com/file/d/${fileId}/preview`;
}

function extractDriveFileId(driveFileLink?: string) {
  if (!driveFileLink) return "";

  const filePathMatch = driveFileLink.match(/\/file\/d\/([^/?]+)/i);
  if (filePathMatch?.[1]) return filePathMatch[1];

  const openIdMatch = driveFileLink.match(/[?&]id=([^&]+)/i);
  if (openIdMatch?.[1]) return openIdMatch[1];

  return "";
}

export function PageTutorial() {
  const [selectedTutorial, setSelectedTutorial] = useState<TutorialItem>(
    tutorialCatalog[0]
  );
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(
    tutorialCatalog[0]?.resources?.[0]?.id || null
  );

  const handleSelectTutorial = (tutorial: TutorialItem) => {
    setSelectedTutorial(tutorial);
    setSelectedResourceId(tutorial.resources?.[0]?.id || null);
  };

  const embeddedTutorialUrl = useMemo(
    () =>
      buildEmbeddedFolderUrl(
        selectedTutorial?.driveLink,
        selectedTutorial?.embedMode || "list"
      ),
    [selectedTutorial]
  );

  const selectedResource = useMemo(() => {
    if (!selectedTutorial?.resources?.length) return null;

    return (
      selectedTutorial.resources.find(
        (resource) => resource.id === selectedResourceId
      ) || selectedTutorial.resources[0]
    );
  }, [selectedResourceId, selectedTutorial]);

  const resourcePreviewUrl = useMemo(
    () =>
      buildDrivePreviewUrl(
        selectedResource?.fileId ||
          extractDriveFileId(selectedResource?.driveFileLink)
      ),
    [selectedResource]
  );

  const hasMappedResources = Boolean(selectedTutorial.resources?.length);

  return (
    <div
      id="page-tutorial"
      className="min-h-screen bg-[linear-gradient(180deg,#fffaf8_0%,#ffffff_38%,#f8f8f8_100%)]"
    >
      <section className="mx-auto flex w-full max-w-[1600px] flex-col gap-5 px-3 pb-8 pt-5 lg:px-5">
        <div className="rounded-2xl border border-gray/15 bg-white px-4 py-4 shadow-sm lg:px-6">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-gray3 lg:text-3xl">
                Central de tutoriais
              </h1>
              <p className="mt-1 max-w-3xl text-sm leading-6 text-gray2">
                Escolha uma trilha para visualizar o material sem sair do sistema.
              </p>
            </div>
            <div className="text-sm text-gray2">
              {tutorialCatalog.length} areas com material
            </div>
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-[340px_minmax(0,1fr)]">
          <TutorialSidebar
            selectedKey={selectedTutorial?.key}
            onSelect={handleSelectTutorial}
          />

          <div className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-gray/20 bg-white shadow-sm">
            <div className="border-b border-gray/15 px-4 py-4 lg:px-6">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-redPrimary">
                    Conteudo selecionado
                  </p>
                  <h3 className="text-xl font-bold text-gray3 lg:text-2xl">
                    {selectedTutorial.title}
                  </h3>
                  <p className="max-w-2xl text-sm leading-6 text-gray2">
                    {selectedTutorial.legend}
                  </p>
                </div>

                {selectedTutorial.driveLink && (
                  <a
                    href={selectedTutorial.driveLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center rounded-full bg-redPrimary px-4 py-2 text-sm font-semibold text-white transition hover:bg-redPrimary/90"
                  >
                    Abrir no Google Drive
                  </a>
                )}
              </div>
            </div>

            <div className="bg-[#fafafa] p-3 lg:p-4">
              <div className="overflow-hidden rounded-xl border border-gray/15 bg-white">
                {hasMappedResources && selectedResource ? (
                  <div className="grid min-h-[calc(100vh-250px)] gap-0 xl:grid-cols-[360px_minmax(0,1fr)]">
                    <div className="border-b border-gray/10 xl:border-b-0 xl:border-r">
                      <div className="border-b border-gray/10 px-4 py-3 text-xs text-gray2">
                        Arquivos do tutorial. Ao clicar, o preview abre no painel
                        ao lado sem sair do sistema.
                      </div>
                      <div className="max-h-[320px] overflow-y-auto xl:max-h-[calc(100vh-320px)]">
                        {selectedTutorial.resources?.map((resource) => {
                          const isSelected = resource.id === selectedResource.id;

                          return (
                            <button
                              key={resource.id}
                              type="button"
                              onClick={() => setSelectedResourceId(resource.id)}
                              className={`flex w-full flex-col gap-1 border-b border-gray/10 px-4 py-3 text-left transition ${
                                isSelected
                                  ? "bg-redPrimary/8"
                                  : "bg-white hover:bg-gray/5"
                              }`}
                            >
                              <span
                                className={`text-sm font-semibold ${
                                  isSelected ? "text-redPrimary" : "text-gray3"
                                }`}
                              >
                                {resource.title}
                              </span>
                              <span className="text-xs uppercase tracking-[0.14em] text-gray2">
                                {resource.type}
                              </span>
                              {resource.description && (
                                <span className="text-xs leading-5 text-gray2">
                                  {resource.description}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="min-w-0">
                      <div className="border-b border-gray/10 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-redPrimary">
                          Preview no SL
                        </p>
                        <h4 className="mt-1 text-base font-semibold text-gray3">
                          {selectedResource.title}
                        </h4>
                        {selectedResource.description && (
                          <p className="mt-1 text-sm text-gray2">
                            {selectedResource.description}
                          </p>
                        )}
                      </div>
                      {resourcePreviewUrl ? (
                        <iframe
                          title={selectedResource.title}
                          src={resourcePreviewUrl}
                          className="h-[calc(100vh-340px)] min-h-[520px] w-full"
                          allow="autoplay"
                          allowFullScreen
                        />
                      ) : (
                        <div className="flex h-[560px] items-center justify-center p-6 text-center text-gray2">
                          Arquivo sem preview configurado. Informe `fileId` ou
                          `driveFileLink` neste recurso.
                        </div>
                      )}
                    </div>
                  </div>
                ) : embeddedTutorialUrl ? (
                  <>
                    <div className="border-b border-gray/10 px-4 py-2 text-xs text-gray2">
                      Visualizacao embutida da pasta do Google Drive.
                    </div>
                    <iframe
                      title={`Tutorial ${selectedTutorial.title}`}
                      src={embeddedTutorialUrl}
                      className="h-[calc(100vh-250px)] min-h-[520px] w-full"
                    />
                  </>
                ) : (
                  <div className="flex h-[560px] items-center justify-center p-6 text-center text-gray2">
                    Nenhum material foi configurado para este tutorial.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
